import type { Patrol } from '@local/common-lib';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useMemo } from 'react';
import { AlertCard } from '../../../components/alert-card/index.js';
import { useAuthenticated } from '../../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../../utils/types.js';
import { ExistingPatrolPage } from '../existing-patrol.js';
import { PatrolProgressContent } from './progress.controller.js';
import { StartPatrolContent } from './start.controller.js';

/*
PURPOSE

- check that patrol is not cancelled
- check patrol start window
- check that user is on patrol
- switch functionality based on started and finished flags

*/

export const RecordPatrolContent: FC<ExistingEntityViewProps<Patrol.Type>> = ({
  data: patrol,
  id,
  refresh,
  fetchNow,
}) => {
  const auth = useAuthenticated();

  const expectedPatrollers = useMemo(() => {
    const patrollers = patrol.patrollers || {};
    return Object.keys(patrollers).filter((patrollerId) => {
      const assignment = patrollers[patrollerId];
      return (
        assignment !== undefined && !assignment.declined && !assignment.removed
      );
    });
  }, [patrol]);

  const isPatrol = !patrol.type || patrol.type === 'patrol';
  if (!isPatrol) {
    return <AlertCard severity="error">Event is not a patrol.</AlertCard>;
  }

  if (patrol.finished) {
    return <AlertCard severity="error">Patrol is already finished.</AlertCard>;
  }
  if (!patrol.finalized) {
    return (
      <AlertCard severity="error">
        Patrol must be finalized to be started.
      </AlertCard>
    );
  }
  if (patrol.cancelled) {
    return <AlertCard severity="error">Patrol has been cancelled.</AlertCard>;
  }
  if (!expectedPatrollers.includes(auth.userInfo.userId)) {
    return (
      <AlertCard severity="error">
        You must be part of a patrol to record information about it.
      </AlertCard>
    );
  }

  if (!patrol.started) {
    const tooEarly =
      DateTime.local() <
        DateTime.fromJSDate(patrol.when).minus({ minutes: 60 }) || false;
    if (tooEarly) {
      return (
        <AlertCard severity="error">
          It is too early to start this patrol. It is scheduled to start at{' '}
          {DateTime.fromJSDate(patrol.when).toLocaleString({
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            month: 'short',
            weekday: 'short',
          })}
          .
        </AlertCard>
      );
    }
    const tooLate =
      DateTime.local() >
        DateTime.fromJSDate(patrol.when).plus({ hours: patrol.hours }) || false;
    if (tooLate) {
      return (
        <AlertCard severity="error">
          It is too late to start this patrol. It was scheduled to start at{' '}
          {DateTime.fromJSDate(patrol.when).toLocaleString({
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            month: 'short',
            weekday: 'short',
            year: 'numeric',
          })}
          .
        </AlertCard>
      );
    }

    return (
      <StartPatrolContent
        data={patrol}
        fetchNow={fetchNow}
        id={id}
        refresh={refresh}
      />
    );
  }

  return (
    <PatrolProgressContent
      data={patrol}
      fetchNow={fetchNow}
      id={id}
      refresh={refresh}
    />
  );
};

export const RecordPatrolPage: FC = () => (
  <ExistingPatrolPage view={RecordPatrolContent} />
);
